import React, { useEffect, useMemo, useState } from "react";

import {
  Paper,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
} from "@mui/material";

import { useClientInfo, axiosInstance } from "../../utils/commonImports";
import dayjs from "dayjs";

export default function OutstandingStatement() {
  const { clientId } = useClientInfo();
  const [customers, setCustomers] = useState([]);

  const [customerId, setCustomerId] = useState("ALL");

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState(
    dayjs().startOf("year").format("YYYY-MM-DD"),
  );

  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));

  // =====================================
  // LOAD CUSTOMERS
  // =====================================

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get("/api/customers", {
        params: {
          client_id: clientId,
        },
      });
      setCustomers(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!clientId) return;

    loadCustomers();
  }, [clientId]);

  // =====================================
  // LOAD REPORT
  // =====================================

  const loadReport = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        "/api/accountant/outstanding-statement",
        {
          params: {
            customer_id: customerId,

            from_date: fromDate,

            to_date: toDate,

            client_id: clientId,
          },
        },
      );

      setRows(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================
  // GRAND TOTAL
  // =====================================

  const grandInvoiceTotal = useMemo(() => {
    return rows.reduce((sum, r) => sum + Number(r.invoice_amount || 0), 0);
  }, [rows]);

  const grandReceivedTotal = useMemo(() => {
    return rows.reduce((sum, r) => sum + Number(r.received_amount || 0), 0);
  }, [rows]);

  const grandBalanceTotal = useMemo(() => {
    return rows.reduce((sum, r) => sum + Number(r.balance || 0), 0);
  }, [rows]);
  // =====================================
  // GROUP DATA
  // =====================================

  const groupedData = useMemo(() => {
    const groups = {};

    rows.forEach((row) => {
      if (!groups[row.customername]) {
        groups[row.customername] = [];
      }

      groups[row.customername].push(row);
    });

    return groups;
  }, [rows]);

  // =====================================
  // UI
  // =====================================

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Outstanding Statement
      </Typography>

      {/* FILTERS */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            md: "2fr 1fr 1fr auto",
          },

          gap: 2,

          mb: 2,
        }}
      >
        {/* CUSTOMER */}

        <TextField
          select
          label="Customer"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          size="small"
        >
          <MenuItem value="ALL">All Customers</MenuItem>

          {customers.map((c) => (
            <MenuItem key={c.customerid} value={c.customerid}>
              {c.customername}
            </MenuItem>
          ))}
        </TextField>

        {/* FROM DATE */}

        <TextField
          type="date"
          label="From Date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
        />

        {/* TO DATE */}

        <TextField
          type="date"
          label="To Date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
        />

        {/* BUTTON */}

        <Button variant="contained" onClick={loadReport}>
          Load
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* TABLE */}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 5,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            {/* HEADER */}

            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#f5f7fa",
                    fontWeight: 700,
                  },
                }}
              >
                <TableCell>Date</TableCell>

                <TableCell>Invoice No</TableCell>

                <TableCell align="right">Invoice Amount</TableCell>

                <TableCell align="right">Received</TableCell>

                <TableCell align="right">Balance</TableCell>

                <TableCell align="right">Days</TableCell>
              </TableRow>
            </TableHead>

            {/* BODY */}

            <TableBody>
              {Object.entries(groupedData).map(([customer, customerRows]) => {
                const customerInvoiceTotal = customerRows.reduce(
                  (sum, r) => sum + Number(r.invoice_amount || 0),
                  0,
                );

                const customerReceivedTotal = customerRows.reduce(
                  (sum, r) => sum + Number(r.received_amount || 0),
                  0,
                );

                const customerBalanceTotal = customerRows.reduce(
                  (sum, r) => sum + Number(r.balance || 0),
                  0,
                );
                return (
                  <React.Fragment key={customer}>
                    {/* CUSTOMER HEADER */}

                    <TableRow
                      sx={{
                        "& td": {
                          backgroundColor: "#e8f0fe",
                          fontWeight: 700,
                        },
                      }}
                    >
                      <TableCell colSpan={6}>{customer}</TableCell>
                    </TableRow>

                    {/* ROWS */}

                    {customerRows.map((row, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          {dayjs(row.invoice_date).format("DD/MM/YYYY")}
                        </TableCell>

                        <TableCell>{row.invoice_number}</TableCell>

                        <TableCell align="right">
                          {Number(row.invoice_amount).toFixed(2)}
                        </TableCell>

                        <TableCell align="right">
                          {Number(row.received_amount).toFixed(2)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,

                            color:
                              Number(row.balance) > 0
                                ? "error.main"
                                : "success.main",
                          }}
                        >
                          {Number(row.balance).toFixed(2)}
                        </TableCell>

                        <TableCell align="right">{row.days}</TableCell>
                      </TableRow>
                    ))}

                    {/* CUSTOMER TOTAL */}

                    <TableRow
                      sx={{
                        "& td": {
                          backgroundColor: "#f8fafc",
                          fontWeight: 700,
                        },
                      }}
                    >
                      <TableCell >{customer}</TableCell>
<TableCell > Total</TableCell>
                      <TableCell align="right">
                        {customerInvoiceTotal.toFixed(2)}
                      </TableCell>

                      <TableCell align="right">
                        {customerReceivedTotal.toFixed(2)}
                      </TableCell>

                      <TableCell align="right">
                        {customerBalanceTotal.toFixed(2)}
                      </TableCell>

                      <TableCell />
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {/* GRAND TOTAL */}

              <TableRow
                sx={{
                  "& td": {
                    backgroundColor: "#dfefff",
                    fontWeight: 700,
                    borderTop: "2px solid #90caf9",
                  },
                }}
              >
                <TableCell colSpan={2}>Grand Total</TableCell>

                <TableCell align="right">
                  {grandInvoiceTotal.toFixed(2)}
                </TableCell>

                <TableCell align="right">
                  {grandReceivedTotal.toFixed(2)}
                </TableCell>

                <TableCell align="right">
                  {grandBalanceTotal.toFixed(2)}
                </TableCell>

                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
